import { useEffect, useState } from "react";
import Table, { TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../../components/Table/Table";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function LocationHeadManagement() {
  const [locations, setLocations] = useState([]);
  const [locationHeadUsers, setLocationHeadUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkStatus, setNetworkStatus] = useState({
    online: typeof window !== 'undefined' ? window.navigator.onLine : true,
    apiReachable: true
  });

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setNetworkStatus(prev => ({ ...prev, online: true }));
    const handleOffline = () => setNetworkStatus(prev => ({ ...prev, online: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check if API is reachable using an existing endpoint
  const checkApiConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${API_BASE_URL}/loc_head/locations`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Any response means the API is reachable, even if it's an error
      setNetworkStatus(prev => ({ ...prev, apiReachable: true }));
      return true;
    } catch (error) {
      console.error('API connection check failed:', error);
      setNetworkStatus(prev => ({ ...prev, apiReachable: false }));
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First check API connection
        const isApiReachable = await checkApiConnection();
        if (!isApiReachable) {
          throw new Error(
            `Unable to connect to server at ${API_BASE_URL}. ` +
            'Please check that the server is running and you are connected to the correct network.'
          );
        }

        const [locationsRes, usersRes] = await Promise.all([
          fetch(`${API_BASE_URL}/loc_head/locations`),
          fetch(`${API_BASE_URL}/loc_head/location-head-users`)
        ]);

        if (!locationsRes.ok) {
          throw new Error(`Failed to fetch locations: ${locationsRes.status}`);
        }
        if (!usersRes.ok) {
          throw new Error(`Failed to fetch users: ${usersRes.status}`);
        }

        const locationsData = await locationsRes.json();
        const usersData = await usersRes.json();

        setLocations(locationsData);
        setLocationHeadUsers(usersData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Error loading data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!networkStatus.online) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
        <p className="text-red-500 font-semibold mb-2">You appear to be offline</p>
        <p className="text-gray-600 mb-4">Please check your internet connection</p>
        <div className="text-sm text-gray-500">
          Trying to connect to: {API_BASE_URL}
        </div>
      </div>
    );
  }

  if (!networkStatus.apiReachable && !loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
        <p className="text-red-500 font-semibold mb-2">Unable to reach the server</p>
        <p className="text-gray-600 mb-4">Please verify:</p>
        <ul className="text-left text-sm text-gray-600 mb-4">
          <li>• The server is running</li>
          <li>• You're connected to the correct network</li>
          <li>• The API URL is correct: {API_BASE_URL}</li>
        </ul>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  const handleAssignment = async (locationId, userId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/loc_head/assign-location-head`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locationId, userId }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const locationsRes = await fetch(`${API_BASE_URL}/loc_head/locations`);
      const updatedLocations = await locationsRes.json();
      setLocations(updatedLocations || []);
    } catch (err) {
      console.error("Assignment Error:", err);
      setError("Failed to assign location head.");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen text-gray-700 font-semibold">Loading...</div>;
  if (error) return <div className="flex items-center justify-center h-screen text-red-500 font-semibold">{error}</div>;

  // Mobile card view for small screens
  const MobileView = () => (
    <div className="space-y-4 md:hidden">
      {locations.map((location) => (
        <div key={location.LocationID} className="bg-white p-4 rounded-lg shadow">
          <div className="space-y-2">
            <div>
              <label className="text-sm font-medium text-gray-500">Location Name</label>
              <p className="text-gray-900">{location.LocationName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Current Location Head</label>
              <p className="text-gray-900">{location.CurrentLocationHead || "Not Assigned"}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Assign New Location Head</label>
              <select
                className="mt-1 w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                defaultValue=""
                onChange={(e) => handleAssignment(location.LocationID, e.target.value)}
              >
                <option value="" disabled>Select Location Head</option>
                {locationHeadUsers.map((user) => (
                  <option key={user.UserID} value={user.UserID}>
                    {user.Username}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Desktop table view
  const DesktopView = () => (
    <div className="hidden md:block overflow-x-auto">
      <Table className="min-w-full bg-white shadow-lg rounded-lg">
        <TableHead className="bg-gray-100">
          <TableRow>
            <TableHeader className="px-6 py-3 text-left text-gray-900 uppercase text-sm tracking-wider">
              Location Name
            </TableHeader>
            <TableHeader className="px-6 py-3 text-left text-gray-900 uppercase text-sm tracking-wider">
              Current Location Head
            </TableHeader>
            <TableHeader className="px-6 py-3 text-left text-gray-900 uppercase text-sm tracking-wider">
              Assign New Location Head
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {locations.map((location) => (
            <TableRow
              key={location.LocationID}
              className="hover:bg-gray-50 transition-colors duration-200"
            >
              <TableCell className="px-6 py-4 border-b border-gray-200 text-gray-900">
                {location.LocationName}
              </TableCell>
              <TableCell className="px-6 py-4 border-b border-gray-200 text-gray-900">
                {location.CurrentLocationHead || "Not Assigned"}
              </TableCell>
              <TableCell className="px-6 py-4 border-b border-gray-200">
                <select
                  className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  defaultValue=""
                  onChange={(e) => handleAssignment(location.LocationID, e.target.value)}
                >
                  <option value="" disabled>Select Location Head</option>
                  {locationHeadUsers.map((user) => (
                    <option key={user.UserID} value={user.UserID}>
                      {user.Username}
                    </option>
                  ))}
                </select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-900">
      <Header />
      <main className="flex-grow container mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <h1 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-6 sm:mb-8">
          Location Head Management
        </h1>
        <MobileView />
        <DesktopView />
      </main>
      <Footer className="bg-gray-100 text-center py-4 text-gray-600 text-sm" />
    </div>
  );
}